/**
 * Pure decision logic for the Convex sync layer.
 * Extracted from ConvexSyncLayer so it can be unit-tested directly.
 */

export type SyncConflictType = 'local-has-data' | 'both-have-data';

export type FirstSyncDecision =
  /** Already synced before — continue with the normal echo/merge flow */
  | { action: 'proceed' }
  /** User cancelled conflict resolution this session — do nothing */
  | { action: 'wait' }
  /** Push local budgets to the empty cloud account (first login) */
  | { action: 'import-local' }
  /** Both sides empty — mark sync as established, nothing to transfer */
  | { action: 'mark-done' }
  /** Cloud has data and local is empty — accept cloud data */
  | { action: 'accept-remote' }
  /** Ambiguous state — ask the user */
  | { action: 'conflict'; type: SyncConflictType };

/**
 * Decides what to do the first time budgets sync after sign-in.
 *
 * `sameUserOrFresh` is true when this browser either never synced with any
 * account before, or last synced with the same account that is signing in now.
 * When a *different* account signs in, we never move data silently — the
 * previous user's local budgets must not leak into another account.
 */
export function decideFirstSync(input: {
  importDone: boolean;
  cancelled: boolean;
  localCount: number;
  cloudCount: number;
  sameUserOrFresh: boolean;
}): FirstSyncDecision {
  if (input.importDone) return { action: 'proceed' };
  if (input.cancelled) return { action: 'wait' };

  const { localCount, cloudCount, sameUserOrFresh } = input;
  if (cloudCount === 0 && localCount === 0) return { action: 'mark-done' };
  if (cloudCount === 0) {
    return sameUserOrFresh
      ? { action: 'import-local' }
      : { action: 'conflict', type: 'local-has-data' };
  }
  if (localCount === 0) return { action: 'accept-remote' };
  // Both sides have data and no sync relationship exists yet (no ID map) —
  // silently overwriting either side could lose data, so ask.
  return { action: 'conflict', type: 'both-have-data' };
}

/**
 * Echo detection: decide whether the reactive query result contains genuine
 * remote changes, or is just an echo of this session's own writes.
 *
 * Echoes are detected by comparing each doc's server-assigned `rev` against
 * the rev recorded when this session pushed it (`pushedRevs`). Docs written
 * before the rev field existed have no rev and count as rev 0.
 */
export function detectRemoteChanges(input: {
  docs: Array<{ _id: string; rev?: number }>;
  pushedRevs: Record<string, number>;
  /** Convex IDs this session knows about (pushed revs + persisted ID map) */
  knownConvexIds: Iterable<string>;
}): { hasRemoteDeletions: boolean; hasRemoteChanges: boolean } {
  const currentDocIds = new Set(input.docs.map((d) => d._id));
  const hasRemoteDeletions = [...input.knownConvexIds].some((id) => !currentDocIds.has(id));
  const hasRemoteChanges =
    hasRemoteDeletions ||
    input.docs.some((doc) => {
      const pushed = input.pushedRevs[doc._id];
      return pushed == null || (doc.rev ?? 0) > pushed;
    });
  return { hasRemoteDeletions, hasRemoteChanges };
}
